/**
 * src/lib/voice/voiceScopes.js — canonical voice contextType taxonomy.
 *
 * SINGLE SOURCE OF TRUTH for what counts as a valid voice scope, which
 * backend handler it routes to, and whether the breadcrumb panel filters
 * strictly on (scope_type, scope_id) or shows it as a catch-all event.
 *
 * Background: pre-2026-05-01 the same taxonomy was hardcoded in three
 * separate files with three different membership lists:
 *   - src/lib/voice/enqueue.js                  (SUPPORTED_SCOPES)
 *   - src/lib/voice/voiceProcessFn.js           (IN_APP_SCOPES)
 *   - src/hooks/useVoiceBreadcrumbs.js          (STRICT_CONTEXTS)
 * They drifted: 'procurement' + 'general' worked at the breadcrumb layer
 * but enqueue rejected them after the V-5 rewire — silently breaking
 * focus-view "general" notes and procurement mic notes. CEO 2026-05-01:
 * "Specially interested that we don't duplicate the lists." This file is
 * the de-duplication.
 *
 * Adding a new scope: add ONE entry to VOICE_SCOPES below. All consumers
 * derive their behavior from this object — no other edits required.
 *
 * Why a code-level constant and not a DB enum:
 * Each scope name is tightly coupled to (a) a backend handler dispatch in
 * voiceProcessFn, (b) a fixed shape for how voice_records / voice_uploads
 * rows are written. This is dispatch-table semantics, not user-editable
 * data, so a JS object beats a DB lookup (no runtime fetch, no RLS
 * surface, lints cleanly, structurally impossible to drift across files
 * once everyone imports from here).
 */

export const VOICE_SCOPES = Object.freeze({
  // Project file upload — voice_uploads table, server-side Whisper, large
  // .m4a/.mp3/.wav from iPhone Files app. Surfaces in
  // VoiceUploadsSection's project list.
  project: {
    handler: 'project_upload',
    visibleInBreadcrumb: true,
    label: 'Project',
  },
  // In-app MediaRecorder capture for an entity. Routes to /api/ai
  // voice.capture, writes voice_records + drives breadcrumbs.
  task: {
    handler: 'in_app_capture',
    visibleInBreadcrumb: true,
    label: 'Task',
  },
  deal: {
    handler: 'in_app_capture',
    visibleInBreadcrumb: true,
    label: 'Deal',
  },
  bundle: {
    handler: 'in_app_capture',
    visibleInBreadcrumb: true,
    label: 'Bundle',
  },
  // Catch-all entity-less note (used in focus view header mic).
  general: {
    handler: 'in_app_capture',
    visibleInBreadcrumb: true,
    label: 'General',
  },
  // Procurement view mic — TICKET-CODEX-3391645900: changed to strict-filtered
  // (visibleInBreadcrumb: true). When this was false, contextMatches() returned
  // true for EVERY voice:record_stop event on the procurement strip, so a
  // project/task/deal mic fired while the procurement page was mounted added a
  // phantom optimistic row that the procurement reload could never clear (it only
  // fetches contextType=procurement rows). Strict filtering means the strip only
  // reacts to events whose contextType === 'procurement', matching the existing
  // behaviour of task / deal / bundle breadcrumb panels.
  procurement: {
    handler: 'in_app_capture',
    visibleInBreadcrumb: true,
    label: 'Procurement',
  },
  // Track I — voice description bound to a specific task_images row.
  // Not a top-level breadcrumb; renders inline under its parent image.
  task_image: {
    handler: 'in_app_capture',
    visibleInBreadcrumb: false,
    label: 'Image description',
  },
});

const ENTRIES = Object.entries(VOICE_SCOPES);

export const ALL_SCOPES = new Set(Object.keys(VOICE_SCOPES));

export const PROJECT_UPLOAD_SCOPES = new Set(
  ENTRIES.filter(([, v]) => v.handler === 'project_upload').map(([k]) => k)
);

export const IN_APP_CAPTURE_SCOPES = new Set(
  ENTRIES.filter(([, v]) => v.handler === 'in_app_capture').map(([k]) => k)
);

/**
 * STRICT_CONTEXTS: scopes where the breadcrumb panel applies strict
 * (scope_type, scope_id) filtering. Catch-all scopes (procurement,
 * task_image) are NOT in this set — events from them appear regardless
 * of the active panel's context.
 */
export const STRICT_CONTEXTS = new Set(
  ENTRIES.filter(([, v]) => v.visibleInBreadcrumb).map(([k]) => k)
);

export function isValidScope(scope) {
  return typeof scope === 'string' && ALL_SCOPES.has(scope);
}

export function getScopeLabel(scope) {
  return VOICE_SCOPES[scope]?.label || 'Voice note';
}
